import {
  doc,
  getDoc,
  getDocFromServer,
  onSnapshot,
  setDoc,
  serverTimestamp,
} from 'firebase/firestore';
import { getFirebaseDb } from '../firebase/config';

const DOC_ID = 'main';

export const DEFAULT_BRANDING = {
  primaryColor: '#000000',
  accentColor: '#C9A227',
  bgColor: '#0a0a0a',
  companyName: 'Fortitude IT Solutions',
  tagline: 'Building Digital Excellence',
  logoUrl: '',
};

export const DEFAULT_NOTIF_SETTINGS = {
  newApplicant: true,
  invoiceOverdue: true,
  teamChatMessage: true,
  contactEnquiry: true,
  projectUpdate: true,
  leaveRequest: true,
  approvalNeeded: true,
  meetingUpdates: true,
  taskUpdates: true,
  documentUpdates: true,
  emailDigest: false,
};

export function normalizeNotifSettings(patch) {
  const raw = patch && typeof patch === 'object' ? patch : {};
  const merged = { ...DEFAULT_NOTIF_SETTINGS, ...raw };
  const legacy = raw.newMessage;
  if (raw.teamChatMessage === undefined && legacy !== undefined) {
    merged.teamChatMessage = legacy;
  }
  if (raw.contactEnquiry === undefined && legacy !== undefined) {
    merged.contactEnquiry = legacy;
  }
  delete merged.newMessage;
  return merged;
}

export const DEFAULT_GEO_SETTINGS = {
  enabled: true,
  lat: '-26.1076',
  lng: '28.0567',
  radius: '200',
  address: 'Sandton, Johannesburg, 2196',
  enforceOnRemote: false,
};

export const DEFAULT_EMAIL_SETTINGS = {
  smtpHost: 'smtp.gmail.com',
  smtpPort: '587',
  senderEmail: 'noreply@fitss.co.za',
  senderName: 'Fortitude IT System',
};

/** Shown on `/login` (public read). Keep in sync with seed `AUTH_USERS` + `employees`. */
export const DEFAULT_LOGIN_PAGE = {
  showQuickFill: true,
  cardTitle: 'Secure Access',
  cardSubtitle: 'Firebase Authentication',
  infoTitle: 'Role-Based Dashboards',
  infoText:
    'Sign in with accounts created by the Firestore seed script. Data syncs in real time from Cloud Firestore.',
  quickFillHint: 'Quick fill — demo emails (password from seed / README)',
  footerBadge: 'Firebase',
  demoAccounts: [
    { role: 'super_admin', email: 'bokang@fitss.co.za', name: 'Bokang' },
    { role: 'super_admin', email: 'thobane@fitss.co.za', name: 'Thobane' },
    { role: 'admin', email: 'naledi@fitss.co.za', name: 'Naledi' },
    { role: 'hr', email: 'precious@fitss.co.za', name: 'Precious' },
    { role: 'mentor', email: 'mandla@fitss.co.za', name: 'Mandla' },
    { role: 'finance', email: 'zanele.f@fitss.co.za', name: 'Zanele' },
    { role: 'employee', email: 'sipho@fitss.co.za', name: 'Sipho' },
    { role: 'employee', email: 'amara@fitss.co.za', name: 'Amara' },
  ],
};

function deepMerge(base, patch) {
  if (!patch || typeof patch !== 'object') return { ...base };
  return { ...base, ...patch };
}

function normalizeLoginPage(raw) {
  const base = { ...DEFAULT_LOGIN_PAGE };
  if (!raw || typeof raw !== 'object') return base;
  const merged = { ...base, ...raw };
  if (Array.isArray(raw.demoAccounts) && raw.demoAccounts.length > 0) {
    merged.demoAccounts = raw.demoAccounts.map((row) => ({
      role: row.role,
      email: row.email,
      name: row.name,
    }));
  }
  return merged;
}

export function normalizePlatformSettings(raw) {
  if (!raw || typeof raw !== 'object') {
    return {
      branding: { ...DEFAULT_BRANDING },
      notifSettings: { ...DEFAULT_NOTIF_SETTINGS },
      geoSettings: { ...DEFAULT_GEO_SETTINGS },
      emailSettings: { ...DEFAULT_EMAIL_SETTINGS },
      loginPage: { ...DEFAULT_LOGIN_PAGE, demoAccounts: [...DEFAULT_LOGIN_PAGE.demoAccounts] },
    };
  }
  return {
    branding: deepMerge(DEFAULT_BRANDING, raw.branding),
    notifSettings: normalizeNotifSettings(raw.notifSettings),
    geoSettings: deepMerge(DEFAULT_GEO_SETTINGS, raw.geoSettings),
    emailSettings: deepMerge(DEFAULT_EMAIL_SETTINGS, raw.emailSettings),
    loginPage: normalizeLoginPage(raw.loginPage),
  };
}

export function platformSettingsRef() {
  return doc(getFirebaseDb(), 'platformSettings', DOC_ID);
}

/**
 * Prefer server so `/login` and public pages are not stuck on an old IndexedDB cache
 * after a founder updates settings (common cause of “changes don’t apply”).
 */
export async function fetchPlatformSettings() {
  const ref = platformSettingsRef();
  let snap;
  try {
    snap = await getDocFromServer(ref);
  } catch {
    snap = await getDoc(ref);
  }
  if (!snap.exists()) return normalizePlatformSettings(null);
  return normalizePlatformSettings(snap.data());
}

/**
 * Real-time updates for public `/login` (unauthenticated). Listeners receive server
 * updates when `platformSettings/main` changes, including from other tabs/devices.
 */
export function subscribePlatformSettings(onNext, onError) {
  return onSnapshot(
    platformSettingsRef(),
    (snap) => {
      const raw = snap.exists() ? snap.data() : null;
      onNext(normalizePlatformSettings(raw));
    },
    (err) => {
      if (typeof onError === 'function') onError(err);
    }
  );
}

export async function savePlatformSettings({
  branding,
  notifSettings,
  geoSettings,
  emailSettings,
  loginPage,
}) {
  const payload = {
    branding: branding || DEFAULT_BRANDING,
    notifSettings: notifSettings || DEFAULT_NOTIF_SETTINGS,
    geoSettings: geoSettings || DEFAULT_GEO_SETTINGS,
    emailSettings: emailSettings || DEFAULT_EMAIL_SETTINGS,
    updatedAt: serverTimestamp(),
  };
  if (loginPage !== undefined) {
    payload.loginPage = normalizeLoginPage(loginPage);
  }
  await setDoc(platformSettingsRef(), payload, { merge: true });
}

export function applyBrandingToDocument(branding) {
  if (typeof document === 'undefined') return;
  const b = branding || DEFAULT_BRANDING;
  const root = document.documentElement;
  root.style.setProperty('--secondary-color', b.accentColor);
  root.style.setProperty('--accent-color', b.accentColor);
  root.style.setProperty('--primary-color', b.primaryColor);
  root.style.setProperty('--background-color', b.bgColor);
}
